import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Building2, ArrowRight, Trash2, PhoneCall, ShieldCheck, CheckCircle2, XCircle,
  Edit3, Send, ClipboardCheck,
} from "lucide-react";
import {
  listFPOInterests, updateFPOInterest, deleteFPOInterest, convertFPOInterest,
  listFPOsByStatus, updateFPODraft, submitFPORegistration, verifyFPO, approveFPO, rejectFPO,
} from "@/lib/api";

const IS_TONE = {
  new: "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]",
  contacted: "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]",
  converted: "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]",
  rejected: "bg-red-100 text-red-700",
};
const RS_TONE = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]",
  verified: "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]",
  approved: "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]",
  rejected: "bg-red-100 text-red-700",
};

export default function AdminFPOWorkflow() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("interests");
  return (
    <div data-testid="admin-fpo-workflow">
      <div className="flex items-center gap-3 mb-2">
        <Building2 className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">{t("admin.nav_fpos")}</h2>
      </div>
      <div className="mt-4 flex gap-2 border-b border-[rgba(6,78,59,0.15)]">
        {[
          { k: "interests", label: t("admin_p3.fpo_interests_title") },
          { k: "registrations", label: t("admin_p3.fpo_registrations_title") },
        ].map((x) => (
          <button
            key={x.k}
            data-testid={`fpo-tab-${x.k}`}
            onClick={() => setTab(x.k)}
            className={`px-4 py-2 -mb-px text-sm font-bold uppercase tracking-[0.14em] border-b-2 transition-colors ${
              tab === x.k
                ? "border-[var(--tr-deep-green)] text-[var(--tr-deep-green)]"
                : "border-transparent text-[var(--tr-muted)] hover:text-[var(--tr-deep-green)]"
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>
      {tab === "interests" ? <FPOInterestsTab /> : <FPORegistrationsTab />}
    </div>
  );
}

// ----------------------- Interests tab -----------------------
function FPOInterestsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");

  const load = () => listFPOInterests(status ? { status } : {}).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const convert = async (id) => {
    if (!window.confirm(t("admin_p3.confirm_convert"))) return;
    try { const r = await convertFPOInterest(id); toast.success(r.profile.name); load(); }
    catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };
  const contacted = async (id) => { try { await updateFPOInterest(id, { status: "contacted" }); load(); } catch { toast.error(t("toast.failed")); } };
  const reject = async (id) => { if (!window.confirm(t("admin_p2.reject_confirm"))) return; try { await updateFPOInterest(id, { status: "rejected" }); load(); } catch { toast.error(t("toast.failed")); } };
  const remove = async (id) => { if (!window.confirm(t("admin.confirm_delete"))) return; await deleteFPOInterest(id); load(); };

  return (
    <div className="mt-6" data-testid="fpo-interests-panel">
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("admin_p3.fpo_interests_body")}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["", "new", "contacted", "converted", "rejected"].map((s) => (
          <button key={s || "all"} data-testid={`fpo-int-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${status === s ? "bg-[var(--tr-deep-green)] text-white" : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"}`}>
            {s || t("admin_p2.filter_all")}
          </button>
        ))}
      </div>
      {!items ? <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
        : items.length === 0 ? <div data-testid="fpo-int-empty" className="tr-card p-10 mt-6 text-center text-[var(--tr-muted)]">{t("admin_p3.empty")}</div>
        : (
          <div className="tr-card overflow-hidden mt-6" data-testid="fpo-int-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tr-sage)]/50">
                  <tr>{["FPO Name", t("admin.col_name"), t("admin.col_phone"), "District", t("admin_p2.col_status"), t("admin_p2.col_created")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>))}
                    <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} data-testid={`fpo-int-row-${it.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                      <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{it.fpo_name}</td>
                      <td className="px-4 py-3">{it.contact_person}</td>
                      <td className="px-4 py-3 font-mono">{it.phone}</td>
                      <td className="px-4 py-3">{it.district}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${IS_TONE[it.status] || "bg-gray-100 text-gray-700"}`}>{it.status}</span></td>
                      <td className="px-4 py-3 text-xs text-[var(--tr-muted)]">{new Date(it.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {it.status === "new" && <button data-testid={`fpo-int-contact-${it.id}`} onClick={() => contacted(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline"><PhoneCall className="w-4 h-4" /></button>}
                        {it.status !== "converted" && <button data-testid={`fpo-int-convert-${it.id}`} onClick={() => convert(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline"><ArrowRight className="w-4 h-4" /> {t("admin_p3.action_convert")}</button>}
                        {it.status !== "rejected" && it.status !== "converted" && <button data-testid={`fpo-int-reject-${it.id}`} onClick={() => reject(it.id)} className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold mr-3 hover:underline">{t("admin_p3.action_reject")}</button>}
                        <button data-testid={`fpo-int-delete-${it.id}`} onClick={() => remove(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-muted)] hover:text-red-700 text-sm font-semibold"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
}

// ----------------------- Registrations tab -----------------------
function FPORegistrationsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null); // profile object being edited

  const load = () => listFPOsByStatus(status ? { status } : {}).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const doVerify = async (id) => { if (!window.confirm(t("admin_p3.confirm_verify"))) return; try { await verifyFPO(id); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const doApprove = async (id) => { if (!window.confirm(t("admin_p3.confirm_approve"))) return; try { await approveFPO(id); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const doReject = async (id) => { const r = window.prompt(t("admin_p3.prompt_reject")); if (!r || r.trim().length < 3) return; try { await rejectFPO(id, r.trim()); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const doSubmit = async (id) => { if (!window.confirm(t("admin_p3.confirm_submit"))) return; try { await submitFPORegistration(id); toast.success(t("admin_p3.action_submit_review")); load(); } catch (err) { const d = err?.response?.data?.detail; if (d?.error === "missing_fields") toast.error(`Missing: ${d.fields.join(", ")}`); else if (d?.error === "consent_required") toast.error(t("admin_p3.consent_note")); else toast.error(t("toast.failed")); } };

  const saveEdit = async (patch) => {
    try {
      await updateFPODraft(editing.id, patch);
      setEditing(null);
      load();
      toast.success(t("toast.saved"));
    } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };

  return (
    <div className="mt-6" data-testid="fpo-registrations-panel">
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("admin_p3.fpo_registrations_body")}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["", "draft", "submitted", "verified", "approved", "rejected"].map((s) => (
          <button key={s || "all"} data-testid={`fpo-reg-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${status === s ? "bg-[var(--tr-deep-green)] text-white" : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"}`}>
            {s || t("admin_p2.filter_all")}
          </button>
        ))}
      </div>
      {!items ? <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
        : items.length === 0 ? <div data-testid="fpo-reg-empty" className="tr-card p-10 mt-6 text-center text-[var(--tr-muted)]">{t("admin_p3.empty")}</div>
        : (
          <div className="tr-card overflow-hidden mt-6" data-testid="fpo-reg-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tr-sage)]/50">
                  <tr>{["FPO Name", "Contact", "District", "Members", t("admin_p2.col_status")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>))}
                    <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => (
                    <tr key={f.id} data-testid={`fpo-reg-row-${f.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                      <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{f.name}</td>
                      <td className="px-4 py-3">{f.contact_person} · <span className="font-mono">{f.phone}</span></td>
                      <td className="px-4 py-3">{f.district}</td>
                      <td className="px-4 py-3">{f.member_count ?? "—"}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${RS_TONE[f.registration_status] || "bg-gray-100 text-gray-700"}`}>{f.registration_status || "—"}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {(f.registration_status === "draft" || f.registration_status === "rejected") && (
                          <>
                            <button data-testid={`fpo-reg-edit-${f.id}`} onClick={() => setEditing(f)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline"><Edit3 className="w-4 h-4" /></button>
                            <button data-testid={`fpo-reg-submit-${f.id}`} onClick={() => doSubmit(f.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline"><Send className="w-4 h-4" /> {t("admin_p3.action_submit_review")}</button>
                          </>
                        )}
                        {f.registration_status === "submitted" && <button data-testid={`fpo-reg-verify-${f.id}`} onClick={() => doVerify(f.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline"><ShieldCheck className="w-4 h-4" /> {t("admin_p3.action_verify")}</button>}
                        {f.registration_status === "verified" && <button data-testid={`fpo-reg-approve-${f.id}`} onClick={() => doApprove(f.id)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline"><CheckCircle2 className="w-4 h-4" /> {t("admin_p3.action_approve")}</button>}
                        {(f.registration_status === "submitted" || f.registration_status === "verified") && <button data-testid={`fpo-reg-reject-${f.id}`} onClick={() => doReject(f.id)} className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold hover:underline"><XCircle className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {editing && <FPOEditModal fpo={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}
    </div>
  );
}

// ------- Edit Modal (minimal) -------
function FPOEditModal({ fpo, onClose, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: fpo.name || "",
    registration_no: fpo.registration_no || "",
    contact_person: fpo.contact_person || "",
    phone: fpo.phone || "",
    email: fpo.email || "",
    district: fpo.district || "",
    state: fpo.state || "",
    address_line: fpo.address_line || "",
    member_count: fpo.member_count || "",
    board_chairperson: fpo.board_chairperson || "",
    bank_name: fpo.bank_name || "",
    bank_account_last4: fpo.bank_account_last4 || "",
    ifsc: fpo.ifsc || "",
    gst_number: fpo.gst_number || "",
    year_incorporated: fpo.year_incorporated || "",
    notes: fpo.notes || "",
    consent_accepted: !!fpo.consent_accepted,
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = () => onSave({
    ...form,
    email: form.email || null,
    member_count: form.member_count ? parseInt(form.member_count) : null,
    year_incorporated: form.year_incorporated ? parseInt(form.year_incorporated) : null,
    bank_account_last4: form.bank_account_last4 || null,
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" data-testid="fpo-edit-modal">
      <div className="tr-card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t("admin_p3.action_edit")}: {fpo.name}</h3>
          <button onClick={onClose} data-testid="fpo-edit-close" className="text-[var(--tr-muted)] hover:text-[var(--tr-deep-green)]"><XCircle className="w-6 h-6" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["name", "FPO Name"], ["registration_no", "Reg / CIN"], ["contact_person", "Contact"],
            ["phone", "Phone"], ["email", "Email"], ["district", "District"], ["state", "State"],
            ["address_line", "Address"], ["member_count", "Members (#)"], ["board_chairperson", "Chairperson"],
            ["gst_number", "GSTIN"], ["year_incorporated", "Incorporated Year"],
          ].map(([k, l]) => (
            <label key={k} className="block">
              <span className="tr-label">{l}</span>
              <input data-testid={`fpo-edit-${k}`} className="tr-input" value={form[k]} onChange={set(k)} />
            </label>
          ))}
          <label className="block md:col-span-2">
            <span className="tr-label">Notes</span>
            <textarea data-testid="fpo-edit-notes" className="tr-input tr-textarea" value={form.notes} onChange={set("notes")} />
          </label>
        </div>
        <label className="mt-4 flex items-start gap-3 cursor-pointer">
          <input type="checkbox" data-testid="fpo-edit-consent" checked={form.consent_accepted} onChange={(e) => setForm((s) => ({ ...s, consent_accepted: e.target.checked }))} className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)]" />
          <span className="text-sm text-[var(--tr-deep-green)]">{t("admin_p3.consent_note")}</span>
        </label>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} data-testid="fpo-edit-cancel" className="tr-pill-btn tr-btn-outline">{t("common.cancel")}</button>
          <button onClick={save} data-testid="fpo-edit-save" className="tr-pill-btn tr-btn-primary"><ClipboardCheck className="w-4 h-4" /> {t("common.save")}</button>
        </div>
      </div>
    </div>
  );
}
